/**
 * Secret Sale storefront price projection + exclusive PDP session UI.
 * - Percent comes only from the validated session (never Liquid-hardcoded)
 * - "Du sparst" only on main PDP price (context=product + show_savings)
 * - Cards/recommendations: price + compare-at only
 * - Projection is reversible on expiry / clear / variant morph
 */
(function (window, document) {
  "use strict";

  var ATTR_BASE = "data-ss-base-price";
  var ATTR_COMPARE = "data-ss-compare-at";
  var ATTR_ELIGIBLE = "data-ss-eligible";
  var ATTR_APPLIED = "data-ss-applied-percent";
  var ATTR_SNAPSHOT = "data-ss-snapshot";
  var ATTR_CONTEXT = "data-ss-context";
  var ATTR_SHOW_SAVINGS = "data-ss-show-savings";
  var ATTR_CARD_BADGE = "data-ss-card-badge";
  var CLASS_PENDING = "sss-price-pending";
  var CLASS_SESSION = "smilodox-secret-sale-session-active";
  var CLASS_PDP_PROMO = "smilodox-secret-sale-pdp-promo";
  var CLASS_PDP_EXCLUDED = "smilodox-secret-sale-pdp-excluded";

  function api() {
    return window.SmilodoxSecretSaleSession || null;
  }

  function inScope() {
    var body = document.body;
    if (!body) return false;
    return (
      body.classList.contains("template-product") ||
      body.classList.contains("template-collection-secret-sale")
    );
  }

  function moneyFormat() {
    return (
      (window.themeVariables &&
        themeVariables.settings &&
        themeVariables.settings.moneyFormat) ||
      (window.Shopify && Shopify.money_format) ||
      "{{amount}} €"
    );
  }

  function formatMoney(cents) {
    var value = Math.max(0, Math.round(Number(cents) || 0));
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      try {
        return Shopify.formatMoney(value, moneyFormat());
      } catch (e) {}
    }
    var amount = (value / 100).toFixed(2).replace(".", ",");
    return amount + " €";
  }

  function parseCents(value) {
    var n = parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
  }

  function shouldUpdateSavings(list) {
    return (
      list.getAttribute(ATTR_CONTEXT) === "product" &&
      list.getAttribute(ATTR_SHOW_SAVINGS) === "true"
    );
  }

  function ensureSnapshot(list) {
    if (list.getAttribute(ATTR_SNAPSHOT) === "1") return;
    list.setAttribute(ATTR_SNAPSHOT, "1");
    list.setAttribute("data-ss-original-html", list.innerHTML);
    list.setAttribute("data-ss-original-class", list.className || "");
    var block = list.closest(".product-price-block");
    if (block) {
      list.setAttribute(
        "data-ss-original-block-class",
        block.className || "",
      );
    }
  }

  function restoreList(list) {
    if (!list || list.getAttribute(ATTR_SNAPSHOT) !== "1") {
      list.classList.remove(CLASS_PENDING);
      return;
    }
    var html = list.getAttribute("data-ss-original-html");
    var cls = list.getAttribute("data-ss-original-class");
    if (html != null) list.innerHTML = html;
    if (cls != null) list.className = cls;
    var block = list.closest(".product-price-block");
    var blockCls = list.getAttribute("data-ss-original-block-class");
    if (block && blockCls != null) block.className = blockCls;
    list.removeAttribute("data-ss-projected");
    list.removeAttribute(ATTR_APPLIED);
    list.removeAttribute(ATTR_SNAPSHOT);
    list.removeAttribute("data-ss-original-html");
    list.removeAttribute("data-ss-original-class");
    list.removeAttribute("data-ss-original-block-class");
    list.classList.remove(CLASS_PENDING);
  }

  /**
   * Shared projection used by price lists and Secret Sale card badges.
   * Matches sale-mode / "Du sparst" total reduction: original (compare-at when
   * higher) → projected Secret Sale price from current Shopify selling price.
   */
  function projectDiscount(baseCents, compareAtCents, sessionPercent) {
    var base = Math.max(0, parseCents(baseCents));
    var compareAt = Math.max(0, parseCents(compareAtCents));
    var pct = Math.max(0, Math.min(90, parseInt(sessionPercent, 10) || 0));
    if (!base || !pct) {
      return {
        sessionPercent: pct,
        projected: 0,
        strikeThrough: 0,
        badgePercent: 0,
      };
    }
    var projected = Math.round(base * (1 - pct / 100));
    var strikeThrough = compareAt > base ? compareAt : base;
    var saved = Math.max(0, strikeThrough - projected);
    var badgePercent =
      strikeThrough > 0 ? Math.round((saved * 100) / strikeThrough) : 0;
    return {
      sessionPercent: pct,
      projected: projected,
      strikeThrough: strikeThrough,
      badgePercent: badgePercent,
    };
  }

  function updateSavings(list, regularCents, saleCents, savingsLabel) {
    var saved = Math.max(0, regularCents - saleCents);
    if (!saved) {
      var existing = list.querySelector(".product-savings");
      if (existing) existing.remove();
      return;
    }
    var pct = Math.round((saved * 100) / regularCents);
    var el = list.querySelector(".product-savings");
    if (!el) {
      el = document.createElement("span");
      el.className = "product-savings";
      list.appendChild(el);
    }
    var label = savingsLabel || "Du sparst";
    el.textContent = label + " " + formatMoney(saved) + " (" + pct + "%)";
  }

  function applyToList(list, percent) {
    if (!list || list.getAttribute(ATTR_ELIGIBLE) === "false") {
      restoreList(list);
      return false;
    }
    var base = parseCents(list.getAttribute(ATTR_BASE));
    if (!base) {
      list.classList.remove(CLASS_PENDING);
      return false;
    }
    var compareAt = parseCents(list.getAttribute(ATTR_COMPARE));
    var projection = projectDiscount(base, compareAt, percent);
    if (!projection.sessionPercent) {
      restoreList(list);
      return false;
    }

    if (
      list.getAttribute(ATTR_APPLIED) === String(projection.sessionPercent) &&
      list.getAttribute("data-ss-projected") === "true"
    ) {
      list.classList.remove(CLASS_PENDING);
      return true;
    }

    ensureSnapshot(list);

    var sale = list.querySelector("sale-price");
    if (!sale) {
      list.classList.remove(CLASS_PENDING);
      return false;
    }

    var projected = projection.projected;
    var strikeThrough = projection.strikeThrough;
    var compareClasses =
      sale.className.indexOf("h6") !== -1
        ? "h7 line-through"
        : "subheading text-subdued line-through";

    sale.classList.add("text-on-sale");
    sale.innerHTML =
      '<span class="sr-only">Sale-Preis</span>' + formatMoney(projected);

    var compare = list.querySelector("compare-at-price");
    if (!compare) {
      compare = document.createElement("compare-at-price");
      compare.className = compareClasses;
      sale.insertAdjacentElement("afterend", compare);
    } else {
      compare.className = compareClasses;
    }
    compare.innerHTML =
      '<span class="sr-only">Regulärer Preis</span>' + formatMoney(strikeThrough);

    if (shouldUpdateSavings(list)) {
      var priorSavings = list.querySelector(".product-savings");
      var savingsLabel = "Du sparst";
      if (priorSavings) {
        var match = priorSavings.textContent.match(/^\s*([^\d€$]+)/);
        if (match && match[1]) savingsLabel = match[1].trim();
      }
      updateSavings(list, strikeThrough, projected, savingsLabel);
    }

    list.classList.add("is-on-sale");
    var block = list.closest(".product-price-block");
    if (block) block.classList.add("is-on-sale");

    list.setAttribute("data-ss-projected", "true");
    list.setAttribute(ATTR_APPLIED, String(projection.sessionPercent));
    list.classList.remove(CLASS_PENDING);
    return true;
  }

  function markPendingIfNeeded(list, active) {
    if (!active || !active.percent) return;
    if (list.getAttribute(ATTR_ELIGIBLE) === "false") return;
    if (list.getAttribute("data-ss-projected") === "true") return;
    if (!list.getAttribute(ATTR_BASE)) return;
    list.classList.add(CLASS_PENDING);
  }

  function syncPrices(root, active) {
    if (!inScope()) return;
    var scope = root && root.querySelectorAll ? root : document;
    var lists = scope.querySelectorAll("price-list[" + ATTR_BASE + "]");
    var i;
    if (!active || !active.percent) {
      for (i = 0; i < lists.length; i++) restoreList(lists[i]);
      return;
    }
    for (i = 0; i < lists.length; i++) {
      markPendingIfNeeded(lists[i], active);
      applyToList(lists[i], active.percent);
    }
  }

  function cardRootForBadge(badge) {
    return (
      badge.closest("product-card") ||
      badge.closest(".product-card-split") ||
      badge.closest(".product-card") ||
      null
    );
  }

  function hideCardBadge(badge) {
    badge.textContent = "";
    badge.setAttribute("hidden", "");
    badge.setAttribute("aria-hidden", "true");
  }

  /**
   * Secret Sale collection cards only.
   * Full-price → session %; Shopify pre-discounted → effective total %
   * (compare-at → projected), same math as price projection / Du sparst.
   * Does not touch the global product_card_show_discount setting.
   */
  function syncCardBadges(root, active) {
    var body = document.body;
    if (!body || !body.classList.contains("template-collection-secret-sale")) return;
    var scope = root && root.querySelectorAll ? root : document;
    var badges = scope.querySelectorAll("[" + ATTR_CARD_BADGE + "]");
    var sessionPct =
      active && active.percent
        ? Math.max(0, Math.min(90, parseInt(active.percent, 10) || 0))
        : 0;
    var i;
    for (i = 0; i < badges.length; i++) {
      var badge = badges[i];
      if (!sessionPct) {
        hideCardBadge(badge);
        continue;
      }
      var card = cardRootForBadge(badge);
      var list = card
        ? card.querySelector("price-list[" + ATTR_BASE + "]")
        : null;
      if (!list || list.getAttribute(ATTR_ELIGIBLE) === "false") {
        hideCardBadge(badge);
        continue;
      }
      var projection = projectDiscount(
        list.getAttribute(ATTR_BASE),
        list.getAttribute(ATTR_COMPARE),
        sessionPct,
      );
      if (!projection.badgePercent || projection.badgePercent < 1) {
        hideCardBadge(badge);
        continue;
      }
      badge.textContent = "-" + projection.badgePercent + "%";
      badge.removeAttribute("hidden");
      badge.setAttribute("aria-hidden", "false");
    }
  }

  function pdpBoxRoot() {
    return document.querySelector("[data-sss-pdp-root]");
  }

  function isPdpExcluded() {
    var body = document.body;
    if (body && body.classList.contains(CLASS_PDP_EXCLUDED)) return true;
    var root = pdpBoxRoot();
    if (root && root.getAttribute("data-sss-pdp-eligible") === "false") return true;
    return false;
  }

  function syncPdpBox(active) {
    var root = pdpBoxRoot();

    if (!root) {
      setBodyPromoState(!!active, false);
      return;
    }

    if (!active || isPdpExcluded()) {
      root.setAttribute("hidden", "");
      setBodyPromoState(!!active, false);
      return;
    }

    var sessionApi = api();
    var codeEl = root.querySelector("[data-sss-pdp-code]");
    var percentEl = root.querySelector("[data-sss-pdp-percent]");
    var clockEl = root.querySelector("[data-sss-pdp-clock]");
    if (codeEl) codeEl.textContent = active.code || "";
    if (percentEl) percentEl.textContent = String(active.percent || 0) + "% Rabatt";
    if (clockEl && sessionApi && sessionApi.formatClock) {
      var clock = sessionApi.formatClock(active.remainingMs);
      clockEl.textContent = clock.mm + ":" + clock.ss;
    }
    root.removeAttribute("hidden");
    setBodyPromoState(true, true);
  }

  function setBodyPromoState(sessionActive, showSsPromo) {
    var body = document.body;
    if (!body) return;
    body.classList.toggle(CLASS_SESSION, !!sessionActive);
    body.classList.toggle(CLASS_PDP_PROMO, !!showSsPromo);
  }

  function getActive() {
    var sessionApi = api();
    return sessionApi && sessionApi.getActive ? sessionApi.getActive() : null;
  }

  function revealCollectionGrid() {
    var body = document.body;
    if (!body || !body.classList.contains("template-collection-secret-sale")) return;
    if (!body.classList.contains("smilodox-secret-sale-is-unlocked")) return;
    if (!body.classList.contains("smilodox-secret-sale-grid-pending")) {
      body.classList.add("smilodox-secret-sale-grid-ready");
      return;
    }
    body.classList.remove("smilodox-secret-sale-grid-pending");
    body.classList.add("smilodox-secret-sale-grid-ready");
  }

  function syncAll(root) {
    var active = getActive();
    syncPrices(root, active);
    syncCardBadges(root, active);
    syncPdpBox(active);
    if (document.body && document.body.classList.contains("template-collection-secret-sale")) {
      window.requestAnimationFrame(function () {
        revealCollectionGrid();
      });
    }
  }

  function onVariantChange() {
    window.requestAnimationFrame(function () {
      syncAll(document);
    });
  }

  function injectCss() {
    if (document.getElementById("sss-exclusive-promo-css")) return;
    var style = document.createElement("style");
    style.id = "sss-exclusive-promo-css";
    style.textContent =
      "body.smilodox-secret-sale-pdp-promo [data-discount-promotion]{display:none!important;}" +
      "price-list.sss-price-pending{opacity:0;}" +
      "body.smilodox-secret-sale-session-active.template-product .product-price-block price-list[data-ss-eligible=\"true\"]:not([data-ss-projected=\"true\"]){opacity:0;}" +
      "price-list[data-ss-projected=\"true\"]{opacity:1;}" +
      "[" +
      ATTR_CARD_BADGE +
      "][hidden]{display:none!important;}";
    document.head.appendChild(style);
  }

  function boot() {
    injectCss();
    syncAll(document);

    if (api() && api().onChange) {
      api().onChange(function () {
        syncAll(document);
      });
    }

    document.addEventListener("variant:change", onVariantChange);

    if (typeof MutationObserver !== "undefined" && inScope()) {
      var timer = null;
      var observer = new MutationObserver(function (mutations) {
        var worth = false;
        for (var i = 0; i < mutations.length; i++) {
          var nodes = mutations[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType !== 1) continue;
            if (
              (node.matches &&
                (node.matches("price-list[" + ATTR_BASE + "]") ||
                  node.matches("[" + ATTR_CARD_BADGE + "]") ||
                  node.matches("[data-sss-pdp-root]") ||
                  node.matches("[data-discount-promotion]"))) ||
              (node.querySelector &&
                (node.querySelector("price-list[" + ATTR_BASE + "]") ||
                  node.querySelector("[" + ATTR_CARD_BADGE + "]") ||
                  node.querySelector("[data-sss-pdp-root]") ||
                  node.querySelector("[data-discount-promotion]")))
            ) {
              worth = true;
              break;
            }
          }
          if (worth) break;
        }
        if (!worth) return;
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          syncAll(document);
        }, 50);
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    if (document.body && document.body.classList.contains("template-product")) {
      window.setInterval(function () {
        syncAll(document);
      }, 1000);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})(window, document);
